<template>
  <a-card
      :bordered="true"
      class="role-distribution-card"
  >
    <a-tag
        v-if="showTag"
        class="role-distribution-card__tag"
        :class="{
        'role-distribution-card__tag--current': tagType === 'current',
      }"
    >
      {{ periodLabel }}
    </a-tag>

    <div class="role-distribution-card__header">
      <div>
        <h3 class="role-distribution-card__title">
          {{ title }}
        </h3>

        <p
            v-if="description"
            class="role-distribution-card__description"
        >
          {{ description }}
        </p>
      </div>

      <QuestionCircleOutlined class="role-distribution-card__help-icon" />
    </div>

    <div class="role-distribution-card__body">
      <DomainsDonutChart
          :items="normalizedItems"
          class="role-distribution-card__donut"
      />

      <div class="role-distribution-card__legend">
        <div
            v-for="item in normalizedItems"
            :key="item.key"
            class="role-distribution-card__legend-row"
        >
          <span
              class="role-distribution-card__legend-mark"
              :style="{ backgroundColor: item.color }"
          ></span>

          <span class="role-distribution-card__legend-text">
            {{ item.label }} — {{ item.value }} чел.
          </span>
        </div>
      </div>
    </div>
  </a-card>
</template>

<script>
import { computed } from 'vue'
import { QuestionCircleOutlined } from '@ant-design/icons-vue'

import DomainsDonutChart from '@/components/Analytics/DomainsDonutChart.vue'

const FALLBACK_COLORS = [
  '#A29BFE',
  '#71E5E0',
  '#D5DBE2',
]

export default {
  name: 'RoleDistributionCard',

  components: {
    QuestionCircleOutlined,
    DomainsDonutChart,
  },

  props: {
    periodLabel: {
      type: String,
      default: 'Было',
    },

    tagType: {
      type: String,
      default: 'previous',
      validator: (value) => ['previous', 'current'].includes(value),
    },

    title: {
      type: String,
      default: 'Распределение ролей',
    },

    description: {
      type: String,
      default: 'Количество человек в каждой группе.',
    },

    items: {
      type: Array,
      default: () => [],
    },

    showTag: {
      type: Boolean,
      default: true,
    },
  },

  setup(props) {
    const normalizedItems = computed(() => {
      return (Array.isArray(props.items) ? props.items : []).map((item, index) => ({
        ...item,
        key: item?.key || `item-${index}`,
        label: item?.label || '',
        value: toNumber(item?.value),
        color: item?.color || FALLBACK_COLORS[index % FALLBACK_COLORS.length],
      }))
    })

    return {
      normalizedItems,
    }
  },
}

function toNumber(value) {
  const number = Number(value)

  return Number.isFinite(number) ? number : 0
}
</script>

<style scoped lang="scss">
.role-distribution-card {
  position: relative;
  min-width: 0;
  border-radius: 8px;
}

.role-distribution-card :deep(.ant-card-body) {
  padding: 18px 16px 16px;
}

.role-distribution-card__tag {
  position: absolute;
  top: -10px;
  left: 20px;
  z-index: 2;
  margin: 0;
  height: 20px;
  padding: 0 8px;
  display: inline-flex;
  align-items: center;
  border-radius: 4px;
  font-size: 12px;
  line-height: 18px;
  background: #ffffff;
  color: rgba(0, 0, 0, 0.65);
  border-color: #d5dbe2;
}

.role-distribution-card__tag--current {
  color: var(--geek-blue);
  border-color: var(--geek-blue-3);
  background-color: var(--geek-blue-1);
}

.role-distribution-card__header {
  min-width: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
}

.role-distribution-card__title {
  margin: 0 0 8px;
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.role-distribution-card__description {
  margin: 0;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.45);
}

.role-distribution-card__help-icon {
  flex: 0 0 auto;
  margin-top: 2px;
  color: rgba(0, 0, 0, 0.25);
  font-size: 14px;
}

.role-distribution-card__body {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 20px;
}

.role-distribution-card__donut {
  width: 128px;
  height: 112px;
  flex: 0 0 128px;
}

.role-distribution-card__legend {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.role-distribution-card__legend-row {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.role-distribution-card__legend-mark {
  width: 10px;
  height: 10px;
  flex: 0 0 auto;
  border-radius: 2px;
}

.role-distribution-card__legend-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: $mobile-breakpoint) {
  .role-distribution-card__body {
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
  }
}
</style>